import React, { useState } from 'react';
import { withStyles, Button, CircularProgress } from '@material-ui/core';
import { compose } from 'recompose';
import WithDrawer from '../../../../../Dispatch/Common/Drawer/WithDrawer';
import { postData } from '../../../../../../../api/componentAPIs';
import { TextFieldWithLabel } from '../../../../../Dispatch/RouteExecution/Pickups/PickupManagement/components/TextField';
import { ALPHA_NUMERIC_REGEX_WITH_SPACING } from '../../../../../Dispatch/RouteExecution/constRegex';
import constants from '../../../../../Dispatch/RouteExecution/DriverPopUp/EditStopDialog/constants';
import { isValidEmail, isValidPhone } from '../../../../../Dispatch/utils/utils';
import styles from './style';
import { MAX_LENGTH_CONSTRAINTS } from '../../../../../Dispatch/RouteExecution/DriverPopUp/common/constants';

const AddEditReceiver = ({
  classes,
  receiverDetails: initialReceiverDetails,
  onClose,
  enqueueSnackbar,
  terminalCode,
  customerCode
}) => {
  const [receiverDetails, setReceiverDetails] = useState({
    receiverId: initialReceiverDetails?.receiverId || null,
    receiverName: initialReceiverDetails?.receiverName || null,
    receiverEmail: initialReceiverDetails?.receiverEmail || null,
    receiverContact: initialReceiverDetails?.receiverContact || null
  });

  const [receiverDetailsError, setReceiverDetailsError] = useState({
    receiverName: null,
    receiverEmail: null,
    receiverContact: null
  });

  const [formError, setFormError] = useState(null);
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);

  const handleChange = (name) => (value) => {
    if (name === 'receiverContact') {
      value = value.replace(/\D+/g, '') || null;
    }
    setReceiverDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value
    }));
    setReceiverDetailsError((prevErrors) => ({
      ...prevErrors,
      [name]: getFieldError(name, value)
    }));
    if (formError && ['receiverContact','receiverEmail'].includes(name)) {
      setFormError(null);
    }
  };

  const getFieldError = (field, value) => {
    switch (field) {
      case 'receiverName':
        return value ? null : '* Required';
      case 'receiverEmail':
        return getReceiverEmailError(value);
      case 'receiverContact':
        return getReceiverNumberError(value);
      default:
        return null;
    }
  };

  const getReceiverEmailError = (value) => {
    if (value && !isValidEmail(value)) {
      return 'Invalid email';
    }
    return null;
  };

  const getReceiverNumberError = (value) => {
    if (value && !isValidPhone(value)) {
      return 'Invalid phone number';
    }
    return null;
  };

  const getErrorInfo = () => {
    const receiverDetailsError = {};
    Object.keys(receiverDetails).forEach((fieldName) => {
      receiverDetailsError[fieldName] = getFieldError(fieldName, receiverDetails[fieldName]);
    });
    return receiverDetailsError;
  };

  const saveReceiverData = () => {
    const errorInfo = getErrorInfo();
    setReceiverDetailsError(errorInfo);
    if (!receiverDetails.receiverContact && !receiverDetails.receiverEmail) {
      setFormError('Please add at least one: contact or email');
      return;
    }
    const error = Object.values(errorInfo).filter(Boolean).length;
    if (!error && !formError) {
      saveReceiver();
    }
  };

  const saveReceiver = () => {
    setIsUpdateInProgress(true);
    const requestData = {
      ...receiverDetails,
      terminalCode,
      customerCode
    };
    const isNewReceiver = !requestData.receiverId;
    postData('SaveCustomerReceiver', requestData).then(
      (response) => {
        if (response && response.status >= 200 && response.status < 400) {
          enqueueSnackbar(` Receiver ${isNewReceiver ? 'created' : 'updated'} successfully`, {
            variant: 'success'
          });
          setIsUpdateInProgress(false);
          onClose(response.data?.receiverId?.toUpperCase());
        } else {
          handleError({ statusCode: response.status, isNewReceiver });
        }
      },
      (_error) => {
        handleError({ isNewReceiver });
      }
    );
  };

  const handleError = ({ statusCode, isNewReceiver }) => {
    if (statusCode !== 403) {
      enqueueSnackbar(`Error while ${isNewReceiver ? 'creating' : 'updating'} receiver`, {
        variant: 'error'
      });
    }
    setIsUpdateInProgress(false);
    onClose();
  };

  const getButtonLabel = () => {
    return receiverDetails.receiverId ? 'Update Receiver' : 'Add Receiver';
  };

  return (
    <div>
      <header className={classes.moduleHeader}>
        {receiverDetails.receiverId ? 'Update Receiver' : 'Add Receiver'}
      </header>
      <div className={classes.formContainer}>
        <TextFieldWithLabel
          id="ReceiverName"
          label="Receiver Name"
          handleChange={handleChange('receiverName')}
          error={receiverDetailsError.receiverName}
          value={receiverDetails.receiverName}
          inputProps={{
            pattern: ALPHA_NUMERIC_REGEX_WITH_SPACING,
            maxLength: MAX_LENGTH_CONSTRAINTS.DELIVERY_PRO_FORM.RECEIVER_NAME
          }}
        />
        <TextFieldWithLabel
          id="receiverContact"
          handleChange={handleChange('receiverContact')}
          value={receiverDetails.receiverContact}
          label="Contact"
          error={receiverDetailsError.receiverContact}
          mask={constants.PHONE_NUMBER_MASK}
        />
        <TextFieldWithLabel
          id="ReceiverEmail"
          label="Email"
          handleChange={handleChange('receiverEmail')}
          error={receiverDetailsError.receiverEmail}
          value={receiverDetails.receiverEmail}
          inputProps={{
            maxLength: MAX_LENGTH_CONSTRAINTS.DELIVERY_PRO_FORM.RECEIVER_EMAIL
          }}
        />
      </div>
      {Boolean(formError) && (
        <p
          className={`MuiFormHelperText-root MuiFormHelperText-error ${classes.errorMessageVisible}`}>
          {formError}
        </p>
      )}
      <div className={classes.actionContainer}>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          onClick={()=>onClose()}
          classes={{ root: classes.actionButton }}
          id="closeButton">
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          type="submit"
          onClick={saveReceiverData}
          disabled={
            isUpdateInProgress ||
            Boolean(Object.values(receiverDetailsError).filter(Boolean).length) ||
            Boolean(formError)
          }
          classes={{
            root: classes.actionButton,
            disabled: classes.actionButtonDisabled
          }}>
          {isUpdateInProgress ? (
            <CircularProgress size={16} classes={{ root: classes.circularProgress }} />
          ) : (
            getButtonLabel()
          )}
        </Button>
      </div>
    </div>
  );
};

export default compose(WithDrawer, withStyles(styles))(AddEditReceiver);
